import { Daycare } from 'model/NewMultipleBooking';
import { Moment } from 'moment-timezone';

export const mergeDaycareDayWithClock = (day: Moment, clock: Moment, tz: string): Moment => {
    const d = day.clone().tz(tz).startOf('day');
    const c = clock.clone().tz(tz);
    return d.clone().hour(c.hour()).minute(c.minute()).second(0).millisecond(0);
};

const getDaycareDurationInMinutes = (daycare: Daycare, fallbackDate: Moment, tz: string): number => {
    if (daycare.from && daycare.to) {
        const diff = daycare.to.clone().tz(tz).diff(daycare.from.clone().tz(tz), 'minutes');
        if (diff > 0) {
            return diff;
        }
    }

    const durationService = daycare.service?.durationService;
    if (durationService && typeof durationService === 'object') {
        const total = (durationService.hours ?? 0) * 60 + (durationService.minutes ?? 0);
        if (total > 0) {
            return total;
        }
    }

    return 8 * 60;
};

const uniqSelectedDays = (selected: Array<Moment>, tz: string): Array<Moment> => {
    const uniqByDay = new Map<string, Moment>();
    selected.forEach(d => {
        const dayStart = d.clone().tz(tz).startOf('day');
        uniqByDay.set(dayStart.format('YYYY-MM-DD'), dayStart);
    });
    return Array.from(uniqByDay.values()).sort((a, b) => a.valueOf() - b.valueOf());
};

const mapSelectedDaysWithClock = (
    selected: Array<Moment>,
    fromClock: Moment,
    toClock: Moment | undefined,
    fallbackDate: Moment,
    daycare: Daycare,
    tz: string
): Array<{ date: Moment; dateEnd: Moment }> => {
    const durationInMinutes = getDaycareDurationInMinutes(daycare, fallbackDate, tz);
    const sortedDays = uniqSelectedDays(selected, tz);

    return sortedDays.map(dayStart => {
        const date = mergeDaycareDayWithClock(dayStart, fromClock, tz);
        const dateEnd = toClock
            ? mergeDaycareDayWithClock(dayStart, toClock, tz)
            : date.clone().add(durationInMinutes, 'minutes');
        return { date, dateEnd };
    });
};

export const normalizeDaycareForSlotSave = (daycare: Daycare, tz: string): Daycare => ({
    ...daycare,
    from: daycare.from?.clone().tz(tz),
    to: daycare.to?.clone().tz(tz),
    selectedDates: daycare.selectedDates?.map(d => d.clone().tz(tz).startOf('day'))
});

export const getDaycareDateSlotsForSave = (
    daycare: Daycare,
    fallbackDate: Moment,
    tz: string,
    isEditMode: boolean
): Array<{ date: Moment; dateEnd: Moment }> => {
    const normalizedDaycare = normalizeDaycareForSlotSave(daycare, tz);
    const fallbackInTz = fallbackDate.clone().tz(tz);

    if (isEditMode) {
        const from = (normalizedDaycare.from || fallbackInTz).clone().tz(tz);
        const to =
            normalizedDaycare.to?.clone().tz(tz) ||
            from.clone().add(getDaycareDurationInMinutes(normalizedDaycare, fallbackInTz, tz), 'minutes');
        return [{ date: from, dateEnd: to }];
    }

    const selected = normalizedDaycare.selectedDates ?? [];
    const fromClock = (normalizedDaycare.from || fallbackInTz).clone().tz(tz);
    const toClock = normalizedDaycare.to?.clone().tz(tz);

    if (selected.length === 0) {
        const to =
            toClock ||
            fromClock.clone().add(getDaycareDurationInMinutes(normalizedDaycare, fallbackInTz, tz), 'minutes');
        return [{ date: fromClock.clone(), dateEnd: to.clone() }];
    }

    return mapSelectedDaysWithClock(selected, fromClock, toClock, fallbackInTz, normalizedDaycare, tz);
};

export const hasMultipleDaycareDates = (daycare: Daycare, fallbackDate: Moment, tz: string): boolean =>
    getDaycareDateSlotsForSave(daycare, fallbackDate, tz, false).length > 1;
